import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

/** Dış kabuk — kart görünümü, yuvarlatılmış köşeler */
const PanelTableCard = ({ isDark, className, children }) => (
  <div
    className={cn(
      "rounded-2xl border overflow-hidden",
      isDark
        ? "border-slate-800 bg-slate-950/50 shadow-xl shadow-black/10"
        : "border-gray-200/90 bg-white shadow-sm",
      className
    )}
  >
    {children}
  </div>
);

/** Üst başlık alanı — ikon, başlık, açıklama, toolbar slot */
const PanelTableHeader = ({
  title,
  icon: Icon,
  description,
  toolbar,
  isDark,
  textClass = "",
  labelClass = "",
  accent = "blue",
  className,
  stackToolbar = false,
  toolbarClassName,
}) => {
  const accentMap = {
    blue: isDark ? "from-blue-950/50 via-slate-900 to-slate-950" : "from-blue-50/80 via-white to-white",
    purple: isDark ? "from-purple-950/40 via-slate-900 to-slate-950" : "from-purple-50/80 via-white to-white",
    teal: isDark ? "from-teal-950/40 via-slate-900 to-slate-950" : "from-teal-50/80 via-white to-white",
    amber: isDark ? "from-amber-950/40 via-slate-900 to-slate-950" : "from-amber-50/80 via-white to-white",
  };
  const iconMap = {
    blue: isDark ? "bg-blue-500/15 text-blue-400" : "bg-blue-100 text-blue-600",
    purple: isDark ? "bg-purple-500/15 text-purple-400" : "bg-purple-100 text-purple-600",
    teal: isDark ? "bg-teal-500/15 text-teal-400" : "bg-teal-100 text-teal-600",
    amber: isDark ? "bg-amber-500/15 text-amber-400" : "bg-amber-100 text-amber-600",
  };

  return (
    <div
      className={cn(
        "relative border-b px-4 sm:px-5 py-4 sm:py-5 bg-gradient-to-br",
        accentMap[accent] || accentMap.blue,
        isDark ? "border-slate-800" : "border-gray-100",
        className
      )}
    >
      <div
        className={cn(
          "flex flex-col gap-4",
          stackToolbar ? "lg:gap-3" : "lg:flex-row lg:items-center lg:justify-between",
        )}
      >
        <div className="flex items-start gap-3 min-w-0">
          {Icon && (
            <div className={cn("p-2.5 rounded-xl shrink-0", iconMap[accent] || iconMap.blue)}>
              <Icon className="w-5 h-5" />
            </div>
          )}
          <div className="min-w-0">
            {title && <h2 className={cn("text-base sm:text-lg font-semibold tracking-tight", textClass)}>{title}</h2>}
            {description && <p className={cn("text-xs sm:text-sm mt-0.5", labelClass)}>{description}</p>}
          </div>
        </div>
        {toolbar && (
          <div
            className={cn(
              stackToolbar ? "w-full min-w-0" : "flex flex-wrap items-center gap-2 shrink-0",
              toolbarClassName,
            )}
          >
            {toolbar}
          </div>
        )}
      </div>
    </div>
  );
};

/** Özet istatistik şeridi */
const PanelTableStats = ({ isDark, items = [], className }) => {
  if (!items.length) return null;
  return (
    <div
      className={cn(
        "grid grid-cols-2 sm:grid-cols-4 gap-px border-b",
        isDark ? "border-slate-800 bg-slate-800" : "border-gray-100 bg-gray-100",
        className
      )}
    >
      {items.map((item) => (
        <div
          key={item.label}
          className={cn(
            "px-4 py-3",
            isDark ? "bg-slate-950/60" : "bg-white"
          )}
        >
          <p className={cn("text-[10px] font-medium uppercase tracking-wider", isDark ? "text-slate-500" : "text-gray-500")}>
            {item.label}
          </p>
          <p className={cn("text-lg font-bold font-mono tabular-nums mt-0.5", item.colorClass || (isDark ? "text-slate-100" : "text-gray-900"))}>
            {item.value}
          </p>
        </div>
      ))}
    </div>
  );
};

/** Tablo sarmalayıcı — yatay kaydırma */
const PanelTableFrame = ({ isDark, children, footer, className }) => (
  <div className={className}>
    <div className={cn("panel-table-scroll overflow-x-auto", isDark ? "panel-table-scroll--dark" : "panel-table-scroll--light")}>
      <Table className="panel-table w-full min-w-[640px]">{children}</Table>
    </div>
    {footer}
  </div>
);

const PanelTableHeadRow = ({ isDark, className, ...props }) => (
  <TableRow
    className={cn(
      "border-b hover:bg-transparent",
      isDark ? "border-slate-800 bg-slate-900/70" : "border-gray-100 bg-gray-50/95",
      className
    )}
    {...props}
  />
);

const PanelTableHead = ({ isDark, className, align = "left", ...props }) => (
  <TableHead
    className={cn(
      "h-11 px-4 text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap",
      align === "center" && "text-center",
      align === "right" && "text-right",
      isDark ? "text-slate-400" : "text-gray-500",
      className
    )}
    {...props}
  />
);

const PanelTableBody = ({ className, ...props }) => (
  <TableBody className={cn("[&_tr:last-child]:border-0", className)} {...props} />
);

const PanelTableRow = ({ isDark, className, selected, ...props }) => (
  <TableRow
    className={cn(
      "group transition-colors duration-150",
      isDark
        ? "border-slate-800/50 hover:bg-slate-800/35 data-[state=selected]:bg-blue-500/10"
        : "border-gray-100 hover:bg-blue-50/50 data-[state=selected]:bg-blue-50",
      selected && (isDark ? "bg-blue-500/10" : "bg-blue-50/80"),
      className
    )}
    data-state={selected ? "selected" : undefined}
    {...props}
  />
);

const PanelTableCell = ({ isDark, className, align = "left", mono, ...props }) => (
  <TableCell
    className={cn(
      "px-4 py-3 text-sm align-middle",
      align === "center" && "text-center",
      align === "right" && "text-right",
      mono && "font-mono text-[13px]",
      isDark ? "text-slate-200" : "text-gray-800",
      className
    )}
    {...props}
  />
);

/** Boş durum satırı */
const PanelTableEmpty = ({
  colSpan,
  isDark,
  icon: Icon,
  title = "Kayıt bulunamadı",
  description,
}) => (
  <PanelTableRow isDark={isDark} className="hover:bg-transparent">
    <TableCell colSpan={colSpan} className="py-16 px-4">
      <div className="flex flex-col items-center justify-center text-center gap-2 max-w-sm mx-auto">
        {Icon && <Icon className={cn("w-10 h-10", isDark ? "text-slate-600" : "text-gray-300")} />}
        <p className={cn("text-sm font-medium", isDark ? "text-slate-300" : "text-gray-700")}>{title}</p>
        {description && (
          <p className={cn("text-xs", isDark ? "text-slate-500" : "text-gray-500")}>{description}</p>
        )}
      </div>
    </TableCell>
  </PanelTableRow>
);

/** Alt bilgi çubuğu */
const PanelTableFooter = ({ isDark, children, className }) => (
  <div
    className={cn(
      "px-4 sm:px-5 py-3 border-t flex flex-wrap items-center justify-between gap-2 text-xs sm:text-sm",
      isDark ? "border-slate-800 bg-slate-900/40 text-slate-400" : "border-gray-100 bg-gray-50/80 text-gray-600",
      className
    )}
  >
    {children}
  </div>
);

/** İşlem butonları grubu — satır sonu */
const PanelTableActions = ({ children, className }) => (
  <div className={cn("flex items-center justify-end gap-0.5 sm:gap-1", className)}>
    {children}
  </div>
);

/** Client-side sayfalama hook */
const usePanelTablePagination = (items, { pageSize: initialPageSize = 15, resetDeps = [] } = {}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const resetFingerprint = useMemo(
    () => (Array.isArray(resetDeps) ? resetDeps.map((v) => String(v)).join("\x1e") : ""),
    [resetDeps],
  );

  useEffect(() => {
    setPage(1);
  }, [resetFingerprint]);

  const total = items?.length ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const safePage = Math.min(Math.max(1, page), totalPages);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  const paginatedItems = useMemo(() => {
    if (!Array.isArray(items) || items.length === 0) return [];
    const start = (safePage - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, safePage, pageSize]);

  const setPageSizeAndReset = (size) => {
    setPageSize(size);
    setPage(1);
  };

  return {
    page: safePage,
    setPage,
    pageSize,
    setPageSize: setPageSizeAndReset,
    total,
    totalPages,
    paginatedItems,
    rangeStart: total === 0 ? 0 : (safePage - 1) * pageSize + 1,
    rangeEnd: total === 0 ? 0 : Math.min(safePage * pageSize, total),
  };
};

/** Alt sayfalama çubuğu */
const PanelTablePagination = ({
  isDark,
  page,
  pageSize,
  total,
  totalPages,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [15, 25, 50, 100],
  loading = false,
  rangeStart,
  rangeEnd,
  className,
}) => {
  const [jumpPage, setJumpPage] = useState(String(page));
  const mutedClass = isDark ? "text-slate-400" : "text-gray-500";
  const inputClass = isDark ? "bg-slate-900 border-slate-700 text-white" : "bg-white border-gray-300";

  useEffect(() => {
    setJumpPage(String(page));
  }, [page]);

  const handlePageJump = () => {
    const next = parseInt(jumpPage, 10);
    if (!Number.isFinite(next) || next < 1 || next > totalPages) return;
    onPageChange(next);
  };

  if (total === 0) return null;

  return (
    <div
      className={cn(
        "px-4 sm:px-5 py-3 border-t flex flex-wrap items-center justify-between gap-3",
        isDark ? "border-slate-800 bg-slate-900/40" : "border-gray-100 bg-gray-50/80",
        className
      )}
    >
      <div className={`text-xs sm:text-sm ${mutedClass}`}>
        {rangeStart != null && rangeEnd != null ? (
          <>
            <span className={isDark ? "text-slate-200" : "text-gray-800"}>{rangeStart}–{rangeEnd}</span>
            {" / "}
            <span>{total.toLocaleString("tr-TR")} kayıt</span>
          </>
        ) : (
          <span>{total.toLocaleString("tr-TR")} kayıt</span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        {totalPages > 1 && (
          <>
            <div className="inline-flex items-center gap-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page <= 1 || loading}
                onClick={() => onPageChange(page - 1)}
                className={`h-8 w-8 p-0 ${isDark ? "border-slate-700 hover:bg-slate-800" : ""}`}
                aria-label="Önceki sayfa"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className={`text-xs sm:text-sm px-1 tabular-nums whitespace-nowrap ${mutedClass}`}>
                {page} / {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page >= totalPages || loading}
                onClick={() => onPageChange(page + 1)}
                className={`h-8 w-8 p-0 ${isDark ? "border-slate-700 hover:bg-slate-800" : ""}`}
                aria-label="Sonraki sayfa"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            <div className={`hidden sm:block w-px h-6 ${isDark ? "bg-slate-700/80" : "bg-gray-200"}`} />

            <div className="inline-flex items-center gap-1.5">
              <span className={`text-xs whitespace-nowrap ${mutedClass}`}>Sayfaya git</span>
              <Input
                type="number"
                min={1}
                max={totalPages}
                value={jumpPage}
                onChange={(e) => setJumpPage(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handlePageJump(); }}
                className={`h-8 w-14 px-2 text-xs text-center tabular-nums ${inputClass}`}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={loading}
                onClick={handlePageJump}
                className={`h-8 px-2.5 text-xs ${isDark ? "border-slate-700 hover:bg-slate-800" : ""}`}
              >
                Git
              </Button>
            </div>
          </>
        )}

        <div className={`${totalPages > 1 ? "hidden sm:block" : ""} w-px h-6 ${isDark ? "bg-slate-700/80" : "bg-gray-200"}`} />

        <div className="inline-flex items-center gap-1.5">
          <span className={`text-xs whitespace-nowrap ${mutedClass}`}>Göster</span>
          <Select
            value={String(pageSize)}
            onValueChange={(value) => onPageSizeChange(parseInt(value, 10))}
            disabled={loading}
          >
            <SelectTrigger className={`h-8 w-[4.5rem] text-xs ${inputClass}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
              {pageSizeOptions.map((opt) => (
                <SelectItem key={opt} value={String(opt)}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

export {
  PanelTableCard,
  PanelTableHeader,
  PanelTableStats,
  PanelTableFrame,
  PanelTableHeadRow,
  PanelTableHead,
  PanelTableBody,
  PanelTableRow,
  PanelTableCell,
  PanelTableEmpty,
  PanelTableFooter,
  PanelTableActions,
  PanelTablePagination,
  usePanelTablePagination,
  TableHeader,
};
